import type {
  ConversationResponse,
  DirectMessage,
  Friend,
  FriendRequest,
  FriendsListResponse,
  InviteWithShow,
  ShowInvite,
  UnreadCountsResponse,
  UserPublic,
  UserSearchResponse,
} from '@runway/shared';
import { themeById } from '@runway/shared';
import { create } from 'zustand';
import { ApiError, api } from '../net/api';
import { hubConnection } from '../net/hub';
import { useSession } from './session';

/**
 * Hub store: everything social that is live. Friends, requests, presence,
 * invites, DM conversations and unread counts, plus the connection status of
 * the Colyseus 'hub' room. REST is the source of truth (refresh() re-syncs);
 * hub events are applied optimistically on top.
 *
 * Nothing in here throws into React. If the hub is down the UI keeps working
 * on REST data and shows a "reconnecting" chip.
 */

export type HubStatus = 'offline' | 'connecting' | 'online';

export type HubNotificationKind = 'invite' | 'dm' | 'friend';
export type HubNotification = {
  id: string;
  kind: HubNotificationKind;
  title: string;
  body: string;
  createdAt: number;
  /** invite: respond from the toast. */
  inviteId?: string;
  showId?: string;
  /** dm / friend: who it is about. */
  userId?: string;
};

export type ConversationMeta = { loaded: boolean; loading: boolean; hasMore: boolean; error: string | null };

/** Shapes accepted for GET /api/messages/:friendId (array or wrapped). */
type MessagesPayload = DirectMessage[] | ConversationResponse;

export type HubState = {
  status: HubStatus;
  /** Reconnect attempt number while status === 'connecting' (0 on first connect). */
  reconnectAttempt: number;
  hubError: string | null;

  friends: Friend[];
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
  /** userIds currently connected to the hub. */
  online: Set<string>;
  /** Every invite we know about: received (pending/answered) and ones we sent. */
  invites: ShowInvite[];
  /** friendId -> messages (oldest first). */
  conversations: Record<string, DirectMessage[]>;
  conversationMeta: Record<string, ConversationMeta>;
  unread: Record<string, number>;
  loaded: { friends: boolean; invites: boolean };
  lastRefreshError: string | null;

  /** Chat drawer UI state (lives here so any page can open it). */
  chatOpen: boolean;
  chatFriendId: string | null;
  /** Actionable toasts (invites, DMs) rendered by the app shell. */
  notifications: HubNotification[];

  connect: () => Promise<void>;
  disconnect: () => void;
  refresh: () => Promise<void>;

  sendInvite: (showId: string, toUserId: string) => Promise<void>;
  respondInvite: (inviteId: string, accept: boolean) => Promise<void>;
  sendDm: (toUserId: string, body: string) => Promise<void>;

  requestFriend: (query: string) => Promise<FriendRequest | Friend | null>;
  acceptFriend: (friendshipId: string) => Promise<void>;
  declineFriend: (friendshipId: string) => Promise<void>;
  cancelRequest: (friendshipId: string) => Promise<void>;
  removeFriend: (friendshipId: string) => Promise<void>;
  searchUsers: (q: string, signal?: AbortSignal) => Promise<UserPublic[]>;

  loadConversation: (friendId: string, opts?: { before?: string }) => Promise<void>;
  markRead: (friendId: string) => Promise<void>;
  openChat: (friendId?: string | null) => void;
  closeChat: () => void;
  dismissNotification: (id: string) => void;

  /** Helpers */
  isOnline: (userId: string) => boolean;
  friendById: (userId: string) => Friend | undefined;
  /** Pending invites addressed to me. */
  pendingInvites: () => ShowInvite[];
  totalUnread: () => number;
};

export const DM_MAX_LENGTH = 500;
const NOTIFICATION_TTL_MS = 12_000;
const INVITE_NOTIFICATION_TTL_MS = 90_000;

let notifSeq = 0;
const notifTimers = new Map<string, ReturnType<typeof setTimeout>>();

function meId(): string | null {
  return useSession.getState().user?.id ?? null;
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

/** The server-side feature is not mounted yet (stub) or the route does not exist. */
function isUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function unwrapList<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) {
    return (data as Record<string, T[]>)[key] as T[];
  }
  return [];
}

function sortByCreated<T extends { createdAt: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function upsertById<T extends { id: string }>(list: T[], item: T): T[] {
  const i = list.findIndex((x) => x.id === item.id);
  if (i === -1) return [...list, item];
  const next = list.slice();
  next[i] = item;
  return next;
}

function mergeMessages(existing: DirectMessage[], incoming: DirectMessage[]): DirectMessage[] {
  const byId = new Map<string, DirectMessage>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) byId.set(m.id, m);
  return sortByCreated(Array.from(byId.values()));
}

function otherParty(m: DirectMessage, me: string): string {
  return m.senderId === me ? m.recipientId : m.senderId;
}

/** Human title for an invite's theme (stored as a THEMES id like 'neon-night', or free text). */
function inviteLabel(inv: ShowInvite): string {
  const raw = (inv.theme ?? '').trim();
  if (!raw) return 'a fashion show';
  return `"${themeById(raw)?.title ?? raw}"`;
}

export const useHub = create<HubState>()((set, get) => {
  // ── notifications ────────────────────────────────────────────────────────
  const pushNotification = (n: Omit<HubNotification, 'id' | 'createdAt'>, ttl: number) => {
    const id = `n${++notifSeq}`;
    const item: HubNotification = { ...n, id, createdAt: Date.now() };
    set((s) => ({ notifications: [...s.notifications.slice(-3), item] }));
    if (ttl > 0) {
      notifTimers.set(
        id,
        setTimeout(() => get().dismissNotification(id), ttl),
      );
    }
    return id;
  };

  const dropInviteNotifications = (inviteId: string) => {
    for (const n of get().notifications) if (n.inviteId === inviteId) get().dismissNotification(n.id);
  };

  // ── hub event wiring (once per module) ───────────────────────────────────
  hubConnection.onStatus((status, info) => {
    set({ status, reconnectAttempt: info.attempt, hubError: info.lastError });
    if (status === 'online') {
      // Re-sync after any (re)connect: events may have been dropped while away.
      void get().refresh();
      const openWith = get().chatFriendId;
      if (openWith) void get().loadConversation(openWith);
    }
  });

  hubConnection.on('presence', ({ online }) => {
    set({ online: new Set(online) });
  });

  hubConnection.on('friend.request', ({ request }) => {
    const me = meId();
    if (!me) return;
    if (request.to.id === me) {
      set((s) => ({ incoming: upsertById(s.incoming, request) }));
      pushNotification(
        { kind: 'friend', title: `${request.from.displayName} wants to be friends`, body: 'Accept from the Friends page.', userId: request.from.id },
        NOTIFICATION_TTL_MS,
      );
    } else {
      set((s) => ({ outgoing: upsertById(s.outgoing, request) }));
    }
  });

  hubConnection.on('friend.accepted', ({ friend }) => {
    // Both sides receive this with the *other* person as `friend`. Only the
    // original requester should be told "they accepted".
    const iAsked = get().outgoing.some((r) => r.to.id === friend.id);
    set((s) => ({
      friends: upsertById(
        s.friends.filter((f) => f.id !== friend.id),
        { ...friend, status: 'accepted', direction: null },
      ),
      incoming: s.incoming.filter((r) => r.from.id !== friend.id && r.to.id !== friend.id),
      outgoing: s.outgoing.filter((r) => r.from.id !== friend.id && r.to.id !== friend.id),
    }));
    if (iAsked) {
      pushNotification({ kind: 'friend', title: `${friend.displayName} accepted your request`, body: 'You are now friends. Say hi!', userId: friend.id }, NOTIFICATION_TTL_MS);
    }
  });

  hubConnection.on('friend.removed', ({ userId }) => {
    set((s) => ({
      friends: s.friends.filter((f) => f.id !== userId),
      incoming: s.incoming.filter((r) => r.from.id !== userId && r.to.id !== userId),
      outgoing: s.outgoing.filter((r) => r.from.id !== userId && r.to.id !== userId),
      chatFriendId: s.chatFriendId === userId ? null : s.chatFriendId,
    }));
  });

  hubConnection.on('dm', ({ message }) => {
    const me = meId();
    if (!me) return;
    const other = otherParty(message, me);
    const s = get();
    const viewing = s.chatOpen && s.chatFriendId === other && document.visibilityState === 'visible';
    const fromThem = message.senderId !== me;
    set((st) => {
      // Our own echo: drop the optimistic placeholder with the same text (if the REST reply has not already).
      const existing = st.conversations[other] ?? [];
      const base = fromThem ? existing : existing.filter((m) => !(m.id.startsWith('tmp-') && m.body === message.body));
      return {
        conversations: { ...st.conversations, [other]: mergeMessages(base, [message]) },
        unread: fromThem && !viewing ? { ...st.unread, [other]: (st.unread[other] ?? 0) + 1 } : st.unread,
      };
    });
    if (fromThem && viewing) void get().markRead(other);
    if (fromThem && !viewing) {
      const from = s.friendById(other);
      pushNotification(
        { kind: 'dm', title: from?.displayName ?? 'New message', body: message.body.length > 120 ? `${message.body.slice(0, 117)}…` : message.body, userId: other },
        NOTIFICATION_TTL_MS,
      );
    }
  });

  hubConnection.on('invite', ({ invite }) => {
    set((s) => ({ invites: upsertById(s.invites, invite) }));
    const me = meId();
    if (invite.to.id === me && invite.status === 'pending') {
      pushNotification(
        {
          kind: 'invite',
          title: `${invite.from.displayName} invited you to a show`,
          body: `Theme: ${inviteLabel(invite)}`,
          inviteId: invite.id,
          showId: invite.showId,
          userId: invite.from.id,
        },
        INVITE_NOTIFICATION_TTL_MS,
      );
    }
  });

  hubConnection.on('invite.update', ({ invite }) => {
    set((s) => ({ invites: upsertById(s.invites, invite) }));
    if (invite.status !== 'pending') dropInviteNotifications(invite.id);
    const me = meId();
    if (invite.from.id === me && invite.status === 'accepted') {
      pushNotification({ kind: 'friend', title: `${invite.to.displayName} is in!`, body: `They accepted your invite to ${inviteLabel(invite)}.`, userId: invite.to.id, showId: invite.showId }, NOTIFICATION_TTL_MS);
    }
  });

  hubConnection.on('error', ({ message, code }) => {
    if (code === 'not_implemented') return; // server half still under construction; stay quiet
    set({ hubError: message });
  });

  // ── the store ────────────────────────────────────────────────────────────
  return {
    status: 'offline',
    reconnectAttempt: 0,
    hubError: null,
    friends: [],
    incoming: [],
    outgoing: [],
    online: new Set<string>(),
    invites: [],
    conversations: {},
    conversationMeta: {},
    unread: {},
    loaded: { friends: false, invites: false },
    lastRefreshError: null,
    chatOpen: false,
    chatFriendId: null,
    notifications: [],

    async connect() {
      await hubConnection.connect();
      // Even if the socket is still coming up, REST data can load right away.
      if (!get().loaded.friends) void get().refresh();
    },

    disconnect() {
      hubConnection.disconnect();
      for (const t of notifTimers.values()) clearTimeout(t);
      notifTimers.clear();
      set({
        status: 'offline',
        reconnectAttempt: 0,
        friends: [],
        incoming: [],
        outgoing: [],
        online: new Set<string>(),
        invites: [],
        conversations: {},
        conversationMeta: {},
        unread: {},
        loaded: { friends: false, invites: false },
        chatOpen: false,
        chatFriendId: null,
        notifications: [],
      });
    },

    async refresh() {
      const me = meId();
      if (!me) return;
      const errors: string[] = [];

      const [friendsRes, invitesRes, unreadRes] = await Promise.all([
        api.get<FriendsListResponse>('/api/friends').catch((err: unknown) => {
          if (!isUnavailable(err)) errors.push(errMessage(err, 'Could not load friends'));
          return null;
        }),
        api.get<{ invites: InviteWithShow[] } | InviteWithShow[]>('/api/invites').catch((err: unknown) => {
          if (!isUnavailable(err)) errors.push(errMessage(err, 'Could not load invites'));
          return null;
        }),
        api.get<UnreadCountsResponse>('/api/messages').catch(() => null),
      ]);

      set((s) => {
        const next: Partial<HubState> = { lastRefreshError: errors[0] ?? null };
        if (friendsRes) {
          next.friends = unwrapList<Friend>(friendsRes, 'friends');
          next.incoming = unwrapList<FriendRequest>(friendsRes, 'incoming');
          next.outgoing = unwrapList<FriendRequest>(friendsRes, 'outgoing');
          next.loaded = { ...s.loaded, friends: true };
          // Friend rows carry a server-side online flag; seed presence from it
          // when the hub has not spoken yet.
          if (s.online.size === 0) {
            const seeded = new Set<string>();
            for (const f of next.friends) if (f.online) seeded.add(f.id);
            if (seeded.size) next.online = seeded;
          }
        }
        if (invitesRes) {
          next.invites = unwrapList<ShowInvite>(invitesRes, 'invites');
          next.loaded = { ...(next.loaded ?? s.loaded), invites: true };
        }
        if (unreadRes && unreadRes.unread && typeof unreadRes.unread === 'object') {
          next.unread = { ...unreadRes.unread };
        }
        return next;
      });
    },

    async sendInvite(showId, toUserId) {
      // Prefer REST (persists + fans out via the hub bus); fall back to the hub
      // message if the route is not mounted yet.
      try {
        const res = await api.post<{ invite: ShowInvite }>('/api/invites', { showId, toUserId });
        if (res?.invite) set((s) => ({ invites: upsertById(s.invites, res.invite) }));
        return;
      } catch (err) {
        if (!isUnavailable(err)) throw err;
      }
      if (!hubConnection.send('invite.send', { showId, to: toUserId })) {
        throw new Error('Not connected — try again in a moment');
      }
    },

    async respondInvite(inviteId, accept) {
      dropInviteNotifications(inviteId);
      const prev = get().invites;
      set((s) => ({
        invites: s.invites.map((i) => (i.id === inviteId ? { ...i, status: accept ? 'accepted' : 'declined' } : i)),
      }));
      try {
        const res = await api.post<{ invite: ShowInvite }>(`/api/invites/${encodeURIComponent(inviteId)}/respond`, { accept });
        if (res?.invite) set((s) => ({ invites: upsertById(s.invites, res.invite) }));
      } catch (err) {
        if (isUnavailable(err) && hubConnection.send('invite.respond', { inviteId, accept })) return;
        set({ invites: prev });
        throw err;
      }
    },

    async sendDm(toUserId, rawBody) {
      const me = meId();
      if (!me) throw new Error('Not signed in');
      const body = rawBody.trim();
      if (!body) return;
      if (body.length > DM_MAX_LENGTH) throw new Error(`Keep it under ${DM_MAX_LENGTH} characters`);

      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const optimistic: DirectMessage = {
        id: tempId,
        senderId: me,
        recipientId: toUserId,
        body,
        createdAt: new Date().toISOString(),
        readAt: null,
      };
      set((s) => ({ conversations: { ...s.conversations, [toUserId]: [...(s.conversations[toUserId] ?? []), optimistic] } }));

      const replaceTemp = (real: DirectMessage | null) =>
        set((s) => {
          const list = (s.conversations[toUserId] ?? []).filter((m) => m.id !== tempId);
          return { conversations: { ...s.conversations, [toUserId]: real ? mergeMessages(list, [real]) : list } };
        });

      try {
        const res = await api.post<{ message: DirectMessage } | DirectMessage>(`/api/messages/${encodeURIComponent(toUserId)}`, { body });
        const real = res && 'message' in res ? res.message : (res as DirectMessage | null);
        replaceTemp(real && typeof real === 'object' && 'id' in real ? real : null);
      } catch (err) {
        if (isUnavailable(err) && hubConnection.send('dm.send', { to: toUserId, body })) {
          // The server will echo the persisted message; keep the optimistic one meanwhile.
          return;
        }
        replaceTemp(null);
        throw new Error(errMessage(err, 'Message not sent'));
      }
    },

    async requestFriend(query) {
      const res = await api.post<{ request?: FriendRequest; friend?: Friend } | FriendRequest>('/api/friends/request', { query: query.trim() });
      if (!res) return null;
      if ('friend' in res && res.friend) {
        const friend = res.friend;
        set((s) => ({ friends: upsertById(s.friends, friend) }));
        return friend;
      }
      const request = ('request' in res ? res.request : (res as FriendRequest)) ?? null;
      if (request && 'id' in request) {
        const me = meId();
        if (request.to?.id === me) set((s) => ({ incoming: upsertById(s.incoming, request) }));
        else set((s) => ({ outgoing: upsertById(s.outgoing, request) }));
      }
      return request;
    },

    async acceptFriend(friendshipId) {
      const req = get().incoming.find((r) => r.id === friendshipId);
      const res = await api.post<{ friend?: Friend }>(`/api/friends/${encodeURIComponent(friendshipId)}/accept`);
      set((s) => {
        const friend: Friend | null =
          res?.friend ??
          (req
            ? { ...req.from, friendshipId: req.id, status: 'accepted', direction: null, since: new Date().toISOString(), online: s.online.has(req.from.id) }
            : null);
        return {
          incoming: s.incoming.filter((r) => r.id !== friendshipId),
          friends: friend ? upsertById(s.friends.filter((f) => f.id !== friend.id), friend) : s.friends,
        };
      });
    },

    async declineFriend(friendshipId) {
      await api.post(`/api/friends/${encodeURIComponent(friendshipId)}/decline`);
      set((s) => ({ incoming: s.incoming.filter((r) => r.id !== friendshipId) }));
    },

    async cancelRequest(friendshipId) {
      // The requester cancels through the same endpoint the addressee declines with.
      await api.post(`/api/friends/${encodeURIComponent(friendshipId)}/decline`);
      set((s) => ({ outgoing: s.outgoing.filter((r) => r.id !== friendshipId) }));
    },

    async removeFriend(friendshipId) {
      const friend = get().friends.find((f) => f.friendshipId === friendshipId);
      await api.del(`/api/friends/${encodeURIComponent(friendshipId)}`);
      set((s) => ({
        friends: s.friends.filter((f) => f.friendshipId !== friendshipId),
        chatFriendId: friend && s.chatFriendId === friend.id ? null : s.chatFriendId,
      }));
    },

    async searchUsers(q, signal) {
      const query = q.trim();
      if (query.length < 2) return [];
      const qs = `?q=${encodeURIComponent(query)}`;
      try {
        const res = await api.get<UserSearchResponse | UserPublic[]>(`/api/users/search${qs}`, { signal });
        return unwrapList<UserPublic>(res, 'users');
      } catch (err) {
        if (!isUnavailable(err)) throw err;
        const res = await api.get<UserSearchResponse | UserPublic[]>(`/api/friends/search${qs}`, { signal });
        return unwrapList<UserPublic>(res, 'users');
      }
    },

    async loadConversation(friendId, opts) {
      const meta = get().conversationMeta[friendId] ?? { loaded: false, loading: false, hasMore: true, error: null };
      if (meta.loading) return;
      set((s) => ({ conversationMeta: { ...s.conversationMeta, [friendId]: { ...meta, loading: true, error: null } } }));
      const params = new URLSearchParams({ limit: '50' });
      if (opts?.before) params.set('before', opts.before);
      try {
        const res = await api.get<MessagesPayload>(`/api/messages/${encodeURIComponent(friendId)}?${params.toString()}`);
        const messages = unwrapList<DirectMessage>(res, 'messages');
        const hasMore = Array.isArray(res) ? messages.length >= 50 : Boolean(res?.hasMore ?? messages.length >= 50);
        set((s) => ({
          conversations: { ...s.conversations, [friendId]: mergeMessages(s.conversations[friendId] ?? [], messages) },
          conversationMeta: { ...s.conversationMeta, [friendId]: { loaded: true, loading: false, hasMore, error: null } },
        }));
      } catch (err) {
        set((s) => ({
          conversationMeta: {
            ...s.conversationMeta,
            [friendId]: { ...meta, loaded: meta.loaded || isUnavailable(err), loading: false, hasMore: false, error: isUnavailable(err) ? null : errMessage(err, 'Could not load messages') },
          },
        }));
      }
    },

    async markRead(friendId) {
      const had = get().unread[friendId] ?? 0;
      const me = meId();
      set((s) => {
        const unread = { ...s.unread };
        delete unread[friendId];
        const list = s.conversations[friendId];
        const now = new Date().toISOString();
        return {
          unread,
          conversations: list && me ? { ...s.conversations, [friendId]: list.map((m) => (m.senderId !== me && !m.readAt ? { ...m, readAt: now } : m)) } : s.conversations,
        };
      });
      if (had === 0 && !get().conversations[friendId]?.some((m) => m.senderId !== me && !m.readAt)) return;
      await api.post(`/api/messages/${encodeURIComponent(friendId)}/read`).catch(() => undefined);
    },

    openChat(friendId) {
      const target = friendId ?? get().chatFriendId ?? get().friends[0]?.id ?? null;
      set({ chatOpen: true, chatFriendId: target });
      if (target) {
        void get().loadConversation(target);
        void get().markRead(target);
        // A DM toast for this friend is now redundant.
        for (const n of get().notifications) if (n.kind === 'dm' && n.userId === target) get().dismissNotification(n.id);
      }
    },

    closeChat() {
      set({ chatOpen: false });
    },

    dismissNotification(id) {
      const t = notifTimers.get(id);
      if (t) clearTimeout(t);
      notifTimers.delete(id);
      set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) }));
    },

    isOnline(userId) {
      return get().online.has(userId);
    },
    friendById(userId) {
      return get().friends.find((f) => f.id === userId);
    },
    pendingInvites() {
      const me = meId();
      return get().invites.filter((i) => i.status === 'pending' && i.to.id === me);
    },
    totalUnread() {
      return Object.values(get().unread).reduce((a, b) => a + b, 0);
    },
  };
});

/**
 * Convenience selectors. The number-returning ones are safe to pass straight to
 * useHub(); selectOnlineFriends returns a new array so use it inside useMemo
 * (or with zustand's useShallow), never directly as a useHub selector.
 */
export const selectPendingInviteCount = (s: HubState): number => {
  const me = useSession.getState().user?.id;
  return s.invites.reduce((n, i) => n + (i.status === 'pending' && i.to.id === me ? 1 : 0), 0);
};
export const selectTotalUnread = (s: HubState): number => Object.values(s.unread).reduce((a, b) => a + b, 0);
export const selectOnlineFriends = (s: HubState): Friend[] => s.friends.filter((f) => s.online.has(f.id));
